import { faTimes } from "@fortawesome/free-solid-svg-icons";
import clsx from "clsx";
import React, { useEffect } from "react";

import Button, { ButtonVariantEnum } from "@/components/atoms/forms/Button";
import { ColorEnum } from "@/enums";
import { type CommonProps } from "@/types";
import { closeDialog, openDialog, renderChildren } from "@/utils/helpers";
import { createId } from "@/utils/helpers";
import { gaClickAttrs } from "@/utils/tracking";

import styles from "./Dialog.module.css";

const ref = "dialog";

interface DialogProps extends Partial<CommonProps> {
  autoOpen?: boolean;
  children: React.ReactNode;
  fullscreen?: boolean;
  header?: React.ReactNode;
  hideCloseButton?: boolean;
  lightbox?: boolean;
  modal?: boolean;
  noHeader?: boolean;
  onClose?: () => void;
}

function closeButton(eventGroup?: string) {
  return (
    <Button
      className={styles.close}
      color={ColorEnum.WHITE}
      formMethod="dialog"
      icon={faTimes}
      rounded
      title="Close"
      variant={ButtonVariantEnum.SOLID}
      onClick={closeDialog}
      {...gaClickAttrs({ eventGroup })}
    >
      <span className="sr-only">Close</span>
    </Button>
  );
}

function useDialog(props: DialogProps) {
  const {
    eventGroup,
    fullscreen,
    lightbox,
    modal,
    onClose,
    hideCloseButton,
    header,
    noHeader = false,
  } = props;
  const className = clsx(
    styles[ref],
    fullscreen && styles.fullscreen,
    lightbox && !fullscreen && styles.lightbox,
    props.className,
    noHeader && styles.noHeader,
  );
  const id = props.id || createId(ref);

  return {
    autoOpen: !!props.autoOpen,
    hideCloseButton,
    children: props.children,
    className,
    eventGroup,
    header,
    id,
    noHeader,
    onClick: (e: any) => {
      if (onClose) {
        onClose();
      }

      if (!modal) {
        closeDialog(e);
      }
    },
  };
}

const Dialog = React.forwardRef<HTMLDialogElement, DialogProps>(
  function Dialog(props, forwardRef) {
    const {
      autoOpen,
      children,
      className,
      eventGroup,
      header,
      hideCloseButton,
      id,
      noHeader,
      onClick,
    } = useDialog(props);

    useEffect(() => {
      if (autoOpen) {
        openDialog(id);
      }
    }, [autoOpen, id, eventGroup]);

    if (!children) {
      return null;
    }

    return (
      <dialog className={className} id={id} ref={forwardRef} onClick={onClick}>
        {!noHeader && (
          <header>
            {header}
            {!hideCloseButton && closeButton()}
          </header>
        )}

        <article>{renderChildren(id, children)}</article>
      </dialog>
    );
  },
);

Dialog.displayName = "Dialog";

export default Dialog;
export { Dialog, type DialogProps };
